import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { DocumentProps } from './useSubmittedDocuments';

export const useDocumentManagement = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  const deleteDocument = async (documentId: string): Promise<boolean> => {
    try {
      setIsProcessing(true);
      
      // Get current user (admin) ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Authentication required to delete documents');
        return false;
      }
      
      // Get document file path for storage deletion
      const { data: document } = await supabase
        .from('user_id_documents')
        .select('file_url')
        .eq('id', documentId)
        .single();
      
      if (!document) {
        toast.error('Document not found');
        return false;
      }
      
      // Delete from Supabase storage first
      const { error: storageError } = await supabase.storage
        .from('identity_docs')
        .remove([document.file_url]);
        
      if (storageError) {
        console.error('Storage deletion error:', storageError);
        // Continue with DB deletion even if storage fails
        // We'll handle orphaned files separately
      }
      
      // Call the database function to delete the document record and add to history
      const { data, error } = await supabase.rpc(
        'delete_identity_document',
        { 
          document_id_param: documentId,
          admin_id_param: user.id
        }
      );
      
      if (error) {
        console.error('Database deletion error:', error);
        
        if (error.code === '23503') { // Foreign key constraint violation
          toast.error('Cannot delete document with verification history. Please contact a database administrator.');
        } else if (error.code === '42702') { // Ambiguous column reference
          toast.error('Database function error. Please contact a database administrator.');
        } else {
          toast.error(`Failed to delete document: ${error.message}`);
        }
        return false;
      }
      
      if (data) {
        toast.success('Document deleted successfully');
      } else {
        toast.error('Failed to delete document');
      }
      
      return data as boolean;
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast.error(error.message || 'Failed to delete document');
      return false;
    } finally {
      setIsProcessing(false);
    }
  };
  
  const updateDocument = async (
    documentId: string, 
    updates: {
      documentTypeId?: string;
      documentNumber?: string;
      issueDate?: Date | null;
      expiryDate?: Date | null;
      notes?: string;
    }
  ): Promise<boolean> => {
    try {
      setIsProcessing(true);
      
      // Get current user (admin) ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Authentication required to update documents');
        return false;
      }
      
      // Get current document data to determine what values to pass
      const { data: currentDoc } = await supabase
        .from('user_id_documents')
        .select('document_type_id, document_number, issue_date, expiry_date, notes')
        .eq('id', documentId)
        .single();

      if (!currentDoc) {
        toast.error('Document not found');
        return false;
      }

      // Prepare parameters using the same logic as Document Number
      const documentTypeId = updates.documentTypeId !== undefined ? updates.documentTypeId : null;
      
      // For document number: undefined means don't change, empty string means clear
      const documentNumber = updates.documentNumber !== undefined ? 
        (updates.documentNumber === '' ? '' : updates.documentNumber) : null;
      
      // For dates: undefined means don't change, null means clear
      // We need to pass a special value to indicate clearing vs not changing
      // Use string dates for database function, null to preserve existing
      const issueDate = updates.issueDate !== undefined ? 
        (updates.issueDate ? updates.issueDate.toISOString().split('T')[0] : '1900-01-01') : null;
      
      const expiryDate = updates.expiryDate !== undefined ? 
        (updates.expiryDate ? updates.expiryDate.toISOString().split('T')[0] : '1900-01-01') : null;
      
      // For notes: undefined means don't change, empty string means clear
      const notes = updates.notes !== undefined ? 
        (updates.notes === '' ? '' : updates.notes) : null;

      console.log('Calling update_identity_document with params:', {
        document_id: documentId,
        admin_id: user.id,
        new_document_type_id: documentTypeId,
        new_document_number: documentNumber,
        new_issue_date: issueDate,
        new_expiry_date: expiryDate,
        new_notes: notes
      });
      
      // Call the database function to update the document record
      const { data, error } = await supabase.rpc(
        'update_identity_document',
        { 
          document_id: documentId,
          admin_id: user.id,
          new_document_type_id: documentTypeId,
          new_document_number: documentNumber,
          new_issue_date: issueDate,
          new_expiry_date: expiryDate,
          new_notes: notes
        }
      );
      
      if (error) {
        console.error('Database update error:', error);
        toast.error(`Failed to update document: ${error.message}`);
        return false;
      }
      
      if (data) {
        toast.success('Document updated successfully');
        return true;
      } else {
        toast.error('Failed to update document');
        return false;
      }
    } catch (error: any) {
      console.error('Error updating document:', error);
      toast.error(error.message || 'Failed to update document');
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    deleteDocument,
    updateDocument,
    isProcessing
  };
};
